// src/pages/Tenant_Bootstrap.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";

import axios from "axios";
import Swal from "sweetalert2";

import SuperAdminNav from "../components/superAdminNav";
import Time from "../components/time";

import { useNavigate, useParams } from "react-router-dom";

import {
  ArrowLeft,
  Building2,
  Database,
  CalendarDays,
  Users as UsersIcon,
  AtSign,
  MapPin,
  Phone,
  User as UserIcon,
  KeyRound,
  Info,
  Boxes,
  BadgeCheck,
  Hash,
  BellRing,
  Siren,
  Plus,
  Trash2,
  MessageSquareText,
} from "lucide-react";

const API = "https://superadmin.nissiindia.com/api";

const DEFAULT_PANIC_VOICE_TEMPLATE =
  "Emergency alert. Guard {id}, {name}, has triggered a panic alert.";

/**
 * Validate the admin password.
 *
 * Password must:
 * - Contain at least 8 characters.
 * - Match at least 3 character groups.
 */
function strongPwd(password) {
  if (!password || password.length < 8) {
    return false;
  }

  const matchedSets = [
    /[a-z]/.test(password),
    /[A-Z]/.test(password),
    /[0-9]/.test(password),
    /[^A-Za-z0-9]/.test(password),
  ].filter(Boolean).length;

  return matchedSets >= 3;
}

/**
 * Identify the product using the tenant database name.
 */
function getProductLabelFromDb(dbName) {
  const db = String(dbName || "").toLowerCase();

  if (db.startsWith("patrolv_t_")) {
    return "Patrol Visit";
  }

  if (db.startsWith("patrol_t_")) {
    return "EBD Patrol";
  }

  if (db.startsWith("sentinel_t_")) {
    return "Sentinel";
  }

  return "Unknown";
}

/**
 * Return an appropriate style for the tenant status.
 */
function getStatusPillClass(status) {
  const normalizedStatus = String(status || "").trim().toUpperCase();

  if (normalizedStatus === "ACTIVE") {
    return "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200";
  }

  if (normalizedStatus === "BLOCKED") {
    return "bg-rose-50 text-rose-700 ring-1 ring-rose-200";
  }

  return "bg-amber-50 text-amber-700 ring-1 ring-amber-200";
}

/**
 * Return product badge styling.
 */
function getProductBadgeClass(productLabel) {
  if (productLabel === "EBD Patrol") {
    return "bg-indigo-50 text-indigo-700 ring-indigo-200";
  }

  if (productLabel === "Patrol Visit") {
    return "bg-fuchsia-50 text-fuchsia-700 ring-fuchsia-200";
  }

  if (productLabel === "Sentinel") {
    return "bg-emerald-50 text-emerald-700 ring-emerald-200";
  }

  return "bg-slate-100 text-slate-700 ring-slate-200";
}

export default function Tenant_Bootstrap() {
  const { id } = useParams();
  const navigate = useNavigate();

  const logoUrlRef = useRef(null);

  const [tenants, setTenants] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  const [numUsers, setNumUsers] = useState(5);
  const [numLicenses, setNumLicenses] = useState(0);
  const [numClients, setNumClients] = useState(0);

  const [companyName, setCompanyName] = useState("");
  const [adminFullName, setAdminFullName] = useState("Administrator");
  const [adminUsername, setAdminUsername] = useState("admin");
  const [adminPassword, setAdminPassword] = useState("");

  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");

  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");
  const [logoPath, setLogoPath] = useState("");

  const [userMap, setUserMap] = useState("google");
  const [payrollEnabled, setPayrollEnabled] = useState(false);
  const [notificationSource, setNotificationSource] = useState("both");

  const [panicFromNumber, setPanicFromNumber] = useState("");
  const [panicRecipientNumbers, setPanicRecipientNumbers] = useState([""]);

  const [
    panicVoiceMessageTemplate,
    setPanicVoiceMessageTemplate,
  ] = useState(DEFAULT_PANIC_VOICE_TEMPLATE);

  const [submitting, setSubmitting] = useState(false);

  /**
   * This value is returned by the backend after bootstrap.
   *
   * Example:
   * RFS  -> 1
   * RFS1 -> 2
   * Next tenant -> 3
   */
  const [allocatedMemberPrefix, setAllocatedMemberPrefix] = useState(null);

  const selected = useMemo(
    () =>
      tenants.find(
        (tenant) => Number(tenant.TenantId) === Number(selectedId)
      ) || null,
    [tenants, selectedId]
  );

  const selectedProductLabel = useMemo(
    () => (selected ? getProductLabelFromDb(selected.DatabaseName) : "Unknown"),
    [selected]
  );

  /**
   * Load available tenants.
   */
  useEffect(() => {
    let mounted = true;

    async function loadTenants() {
      try {
        const response = await axios.get(`${API}/tenants`);

        if (!mounted) {
          return;
        }

        const tenantList = Array.isArray(response.data) ? response.data : [];

        setTenants(tenantList);

        if (id) {
          const tenantId = Number(id);

          if (Number.isInteger(tenantId) && tenantId > 0) {
            setSelectedId(tenantId);
          }
        }
      } catch (error) {
        console.error("Load tenant error:", error);

        Swal.fire(
          "Error",
          error?.response?.data?.error || "Unable to load tenants",
          "error"
        );
      }
    }

    loadTenants();

    return () => {
      mounted = false;
    };
  }, [id]);

  /**
   * Automatically populate the company name when a tenant is selected.
   */
  useEffect(() => {
    if (!selected) {
      return;
    }

    setCompanyName(selected.Name || selected.TenantCode || "");

    /*
      Reset a previously displayed prefix when another tenant
      is selected.
    */
    setAllocatedMemberPrefix(
      selected.MemberPrefix ? Number(selected.MemberPrefix) : null
    );
  }, [selected]);

  /**
   * Release the temporary browser URL used for logo preview.
   */
  useEffect(() => {
    return () => {
      if (logoUrlRef.current) {
        URL.revokeObjectURL(logoUrlRef.current);
        logoUrlRef.current = null;
      }
    };
  }, []);

  /**
   * Upload the company logo before the main bootstrap request.
   */
  async function uploadLogoIfAny(tenantId, username) {
    if (!logoFile) {
      return null;
    }

    const formData = new FormData();

    formData.append("tenantId", String(tenantId));
    formData.append("username", username || adminUsername || "admin");
    formData.append("file", logoFile);

    const response = await axios.post(
      `${API}/tenant/mysql/logo`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      }
    );

    return response.data?.logoPath || null;
  }

  /**
   * Validate the tenant bootstrap form.
   */
  function validateForm() {
    if (!selected) {
      Swal.fire("Validation", "Please choose a tenant", "warning");
      return false;
    }

    if (!String(selected.TenantCode || "").trim()) {
      Swal.fire(
        "Validation",
        "The selected tenant does not have a tenant code.",
        "warning"
      );
      return false;
    }

    if (!companyName.trim()) {
      Swal.fire("Validation", "Company name is required", "warning");
      return false;
    }

    if (!adminUsername.trim()) {
      Swal.fire("Validation", "Admin username is required", "warning");
      return false;
    }

    if (!strongPwd(adminPassword)) {
      Swal.fire(
        "Weak password",
        "Password must be at least 8 characters and include 3 of: lowercase, uppercase, digit, or symbol.",
        "warning"
      );
      return false;
    }

    if (email.trim() && !/^\S+@\S+\.\S+$/.test(email.trim())) {
      Swal.fire("Validation", "Invalid email address", "warning");
      return false;
    }

    if (!Number.isInteger(Number(numUsers)) || Number(numUsers) < 1) {
      Swal.fire("Validation", "User limit must be at least 1.", "warning");
      return false;
    }

    if (!Number.isInteger(Number(numClients)) || Number(numClients) < 0) {
      Swal.fire(
        "Validation",
        "Client limit must be zero or a positive integer.",
        "warning"
      );
      return false;
    }

    if (!Number.isInteger(Number(numLicenses)) || Number(numLicenses) < 0) {
      Swal.fire(
        "Validation",
        "Sentinel license limit must be zero or a positive integer.",
        "warning"
      );
      return false;
    }

    if (
      !["whatsapp", "mail", "both"].includes(
        String(notificationSource || "").trim().toLowerCase()
      )
    ) {
      Swal.fire(
        "Validation",
        "Please choose WhatsApp, Mail, or Both as the notification source.",
        "warning"
      );
      return false;
    }

    const normalizedFromNumber = String(panicFromNumber || "")
      .replace(/[\s()-]/g, "")
      .trim();

    if (
      normalizedFromNumber &&
      !/^\+[1-9]\d{7,14}$/.test(normalizedFromNumber)
    ) {
      Swal.fire(
        "Validation",
        "Plivo panic From Number must be in E.164 format, for example +14155552671.",
        "warning"
      );
      return false;
    }

    const normalizedRecipients = panicRecipientNumbers
      .map((number) => String(number || "").replace(/[\s()-]/g, "").trim())
      .filter(Boolean);

    const invalidRecipient = normalizedRecipients.find(
      (number) => !/^\+[1-9]\d{7,14}$/.test(number)
    );

    if (invalidRecipient) {
      Swal.fire(
        "Validation",
        `Invalid panic recipient number: ${invalidRecipient}. Use E.164 format.`,
        "warning"
      );
      return false;
    }

    if (
      normalizedRecipients.length !== new Set(normalizedRecipients).size
    ) {
      Swal.fire(
        "Validation",
        "Duplicate panic recipient numbers are not allowed.",
        "warning"
      );
      return false;
    }

    if (
      panicVoiceMessageTemplate &&
      panicVoiceMessageTemplate.trim().length > 500
    ) {
      Swal.fire(
        "Validation",
        "Panic call voice message must be 500 characters or fewer.",
        "warning"
      );
      return false;
    }

    return true;
  }

  /**
   * Create or update the selected tenant company and admin record.
   *
   * The frontend does not send memberPrefix.
   * The backend allocates the next central prefix automatically.
   */
  async function onSubmit(event) {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      let uploadedLogo = null;

      if (logoFile) {
        uploadedLogo = await uploadLogoIfAny(
          selected.TenantId,
          adminUsername.trim()
        );

        setLogoPath(uploadedLogo || "");
      }

      const payload = {
        tenantId: Number(selected.TenantId),
        tenantCode: String(selected.TenantCode || "").trim(),
        companyName: companyName.trim(),
        adminFullName: adminFullName.trim() || "Administrator",
        adminUsername: adminUsername.trim(),
        adminPassword,
        email: email.trim() || null,
        address: address.trim() || null,
        phone: phone.trim() || null,
        numUsers: Number(numUsers),
        numClients: Number(numClients),
        numLicenses: Number(numLicenses),
        userMap: userMap || "google",
        payrollEnabled,
        notificationSource: String(notificationSource || "both")
          .trim()
          .toLowerCase(),

        panicSettings: {
          fromNumber:
            String(panicFromNumber || "").replace(/[\s()-]/g, "").trim() ||
            null,

          recipientNumbers: panicRecipientNumbers
            .map((number) => String(number || "").replace(/[\s()-]/g, "").trim())
            .filter(Boolean),

          voiceMessageTemplate:
            panicVoiceMessageTemplate.trim() || null,
        },

        logo: uploadedLogo || logoPath || null,
      };

      const response = await axios.post(
        `${API}/tenant/mysql/bootstrap`,
        payload
      );

      const result = response.data || {};

      const generatedPrefix = Number(result.memberPrefix);

      if (Number.isInteger(generatedPrefix) && generatedPrefix > 0) {
        setAllocatedMemberPrefix(generatedPrefix);
      }

      if (result.panicSettings?.voiceMessageTemplate) {
        setPanicVoiceMessageTemplate(
          result.panicSettings.voiceMessageTemplate
        );
      }

      const isSentinel = selectedProductLabel === "Sentinel";

      let successHtml = "<div style='text-align:left'>";

      successHtml +=
        "<p style='margin:0 0 8px'>Tenant bootstrapped successfully.</p>";

      successHtml += `<p style='margin:4px 0'><strong>Tenant Code:</strong> ${
        result.tenantCode || selected.TenantCode
      }</p>`;

      successHtml += `<p style='margin:4px 0'><strong>Database:</strong> ${
        result.database || selected.DatabaseName
      }</p>`;

      if (Number.isInteger(generatedPrefix) && generatedPrefix > 0) {
        successHtml += `<p style='margin:4px 0'><strong>Member Prefix:</strong> ${generatedPrefix}</p>`;

        if (isSentinel) {
          successHtml += `<p style='margin:4px 0'><strong>First Sentinel Member ID:</strong> ${generatedPrefix}G1</p>`;
        }
      }

      successHtml += "</div>";

      await Swal.fire({
        title: "Success",
        html: successHtml,
        icon: "success",
        confirmButtonText: "Continue",
      });

      navigate("/superAdmin/admin_Menu");
    } catch (error) {
      console.error("Tenant bootstrap error:", error);

      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error.message ||
        "Bootstrap failed";

      Swal.fire("Error", message, "error");
    } finally {
      setSubmitting(false);
    }
  }

  /**
   * Handle company logo selection and preview.
   */
  function handleLogoChange(event) {
    const file = event.target.files?.[0] || null;

    setLogoFile(file);

    if (logoUrlRef.current) {
      URL.revokeObjectURL(logoUrlRef.current);
      logoUrlRef.current = null;
    }

    if (!file) {
      setLogoPreview("");
      return;
    }

    const allowedTypes = new Set([
      "image/png",
      "image/jpeg",
      "image/jpg",
      "image/webp",
    ]);

    if (!allowedTypes.has(file.type)) {
      setLogoFile(null);
      setLogoPreview("");

      Swal.fire(
        "Invalid logo",
        "Only PNG, JPG, JPEG, and WEBP images are allowed.",
        "warning"
      );

      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setLogoFile(null);
      setLogoPreview("");

      Swal.fire(
        "Logo too large",
        "The logo must be 2MB or smaller.",
        "warning"
      );

      return;
    }

    const previewUrl = URL.createObjectURL(file);

    logoUrlRef.current = previewUrl;

    setLogoPreview(previewUrl);
  }

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="flex flex-col md:flex-row md:h-screen md:overflow-hidden">
        <SuperAdminNav />

        <main className="w-full md:w-3/4 md:h-full md:overflow-auto">
          <div className="bg-gradient-to-r from-indigo-600 via-blue-600 to-cyan-500 text-white">
            <div className="px-4 sm:px-6 lg:px-8 py-5">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/30">
                    <Building2 className="h-5 w-5" />
                  </div>

                  <div>
                    <h1 className="text-2xl font-semibold leading-tight">
                      Bootstrap Tenant
                    </h1>

                    <p className="text-white/80 text-sm">
                      Create the company, administrator and tenant licensing details
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="inline-flex items-center gap-2 rounded-lg bg-white/15 px-3 py-1.5 text-white ring-1 ring-white/30 hover:bg-white/20"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back
                  </button>

                  <div className="text-sm text-white/90">
                    <Time />
                  </div>
                </div>
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs ring-1 ring-white/30">
                  <BadgeCheck className="h-4 w-4" />
                  Validate & Create
                </span>

                <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs ring-1 ring-white/30">
                  <Boxes className="h-4 w-4" />
                  Module Access
                </span>

                <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs ring-1 ring-white/30">
                  <Hash className="h-4 w-4" />
                  Automatic Member Prefix
                </span>
              </div>
            </div>
          </div>

          <div className="px-4 sm:px-6 lg:px-8 py-6">
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
              <div className="xl:col-span-1 rounded-2xl border border-slate-200 bg-white shadow-sm">
                <div className="flex items-center gap-2 border-b border-slate-200 bg-gradient-to-r from-indigo-50 to-blue-50 px-4 py-3">
                  <Building2 className="h-4 w-4 text-indigo-600" />

                  <h3 className="text-sm font-semibold text-slate-800">
                    Select Tenant
                  </h3>
                </div>

                <div className="p-4 space-y-3">
                  <label className="text-sm font-medium text-slate-700">
                    Tenant *
                  </label>

                  <select
                    value={selectedId || ""}
                    onChange={(event) => {
                      const value = Number(event.target.value);

                      setSelectedId(
                        Number.isInteger(value) && value > 0 ? value : null
                      );
                    }}
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:outline-none"
                    disabled={submitting}
                  >
                    <option value="">-- choose --</option>

                    {tenants.map((tenant) => (
                      <option key={tenant.TenantId} value={tenant.TenantId}>
                        {tenant.TenantCode} — {tenant.Name}
                      </option>
                    ))}
                  </select>

                  {selected ? (
                    <div className="mt-3 grid grid-cols-1 gap-3">
                      <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                        <div className="text-xs text-slate-500">Database</div>

                        <div className="text-sm font-medium text-slate-800">
                          <span className="inline-flex items-center gap-2">
                            <Database className="h-4 w-4 text-slate-500" />
                            {selected.DatabaseName}
                          </span>
                        </div>

                        <div className="mt-2">
                          <span
                            className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ring-1 ${getProductBadgeClass(
                              selectedProductLabel
                            )}`}
                          >
                            {selectedProductLabel}
                          </span>
                        </div>
                      </div>

                      <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                        <div className="text-xs text-slate-500">
                          Plan / Max / Expires
                        </div>

                        <div className="text-sm font-medium text-slate-800">
                          {selected.PlanCode ?? "-"} •{" "}
                          {selected.MaxUsers ?? "-"} users •{" "}
                          {selected.ExpiresOn ?? "-"}
                        </div>
                      </div>

                      <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                        <div className="text-xs text-slate-500">Status</div>

                        <div className="mt-1">
                          <span
                            className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${getStatusPillClass(
                              selected.EffectiveStatus
                            )}`}
                          >
                            {selected.EffectiveStatus}
                          </span>
                        </div>
                      </div>

                      <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-3">
                        <div className="flex items-center gap-2 text-xs font-semibold text-blue-700">
                          <Hash className="h-4 w-4" />
                          Member Prefix
                        </div>

                        <div className="mt-1 text-sm font-bold text-blue-900">
                          {allocatedMemberPrefix
                            ? allocatedMemberPrefix
                            : "Will be generated automatically"}
                        </div>

                        {allocatedMemberPrefix &&
                        selectedProductLabel === "Sentinel" ? (
                          <div className="mt-1 text-xs text-blue-700">
                            First Member ID:{" "}
                            <strong>{allocatedMemberPrefix}G1</strong>
                          </div>
                        ) : (
                          <div className="mt-1 text-xs text-blue-700">
                            The server will use the next available central prefix.
                          </div>
                        )}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>

              <form
                className="xl:col-span-2 rounded-2xl border border-slate-200 bg-white shadow-sm"
                onSubmit={onSubmit}
              >
                <div className="flex items-center justify-between border-b border-slate-200 bg-gradient-to-r from-cyan-50 to-teal-50 px-4 py-3">
                  <div className="flex items-center gap-2">
                    <UserIcon className="h-4 w-4 text-cyan-600" />

                    <h3 className="text-sm font-semibold text-slate-800">
                      Company & Admin Details
                    </h3>
                  </div>

                  <div className="text-xs text-slate-600 flex items-center gap-1">
                    <Info className="h-3.5 w-3.5" />
                    Strong passwords required
                  </div>
                </div>

                <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="mb-1 block text-sm font-medium text-slate-700">
                      Company Name *
                    </label>

                    <div className="relative">
                      <Building2 className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

                      <input
                        value={companyName}
                        onChange={(event) => setCompanyName(event.target.value)}
                        placeholder="e.g. Omega Tech Pvt Ltd"
                        disabled={submitting}
                        className="w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 py-2 text-slate-900 placeholder:text-slate-400 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-200"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">
                      Admin Full Name
                    </label>

                    <div className="relative">
                      <UserIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

                      <input
                        value={adminFullName}
                        onChange={(event) => setAdminFullName(event.target.value)}
                        placeholder="Administrator"
                        disabled={submitting}
                        className="w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 py-2 text-slate-900 placeholder:text-slate-400 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-200"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">
                      Admin Username *
                    </label>

                    <input
                      value={adminUsername}
                      onChange={(event) => setAdminUsername(event.target.value)}
                      placeholder="admin"
                      disabled={submitting}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 placeholder:text-slate-400 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-200"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="mb-1 block text-sm font-medium text-slate-700">
                      Admin Password *
                    </label>

                    <div className="relative">
                      <KeyRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

                      <input
                        type="password"
                        value={adminPassword}
                        onChange={(event) => setAdminPassword(event.target.value)}
                        placeholder="StrongP@ssw0rd!"
                        disabled={submitting}
                        autoComplete="new-password"
                        className="w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 py-2 text-slate-900 placeholder:text-slate-400 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-200"
                      />
                    </div>

                    <p className="mt-1 text-xs text-slate-500">
                      At least 8 characters and 3 of: lowercase, uppercase, digit or symbol.
                    </p>
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">
                      Phone
                    </label>

                    <div className="relative">
                      <Phone className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

                      <input
                        value={phone}
                        onChange={(event) => setPhone(event.target.value)}
                        placeholder="+91 90000 00000"
                        disabled={submitting}
                        className="w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 py-2 text-slate-900 placeholder:text-slate-400 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-200"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">
                      Email
                    </label>

                    <div className="relative">
                      <AtSign className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

                      <input
                        type="email"
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                        placeholder="admin@example.com"
                        disabled={submitting}
                        className="w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 py-2 text-slate-900 placeholder:text-slate-400 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-200"
                      />
                    </div>
                  </div>

                  <div className="md:col-span-2">
                    <label className="mb-1 block text-sm font-medium text-slate-700">
                      Address
                    </label>

                    <div className="relative">
                      <MapPin className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />

                      <input
                        value={address}
                        onChange={(event) => setAddress(event.target.value)}
                        placeholder="Company address"
                        disabled={submitting}
                        className="w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 py-2 text-slate-900 placeholder:text-slate-400 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-200"
                      />
                    </div>
                  </div>

                  <div className="md:col-span-2">
                    <label className="mb-1 block text-sm font-medium text-slate-700">
                      User Limit (num_users)
                    </label>

                    <div className="relative">
                      <Boxes className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

                      <input
                        type="number"
                        min={1}
                        step={1}
                        value={numUsers}
                        onChange={(event) => {
                          const value = Number(event.target.value);

                          setNumUsers(
                            Number.isInteger(value) && value >= 1 ? value : 1
                          );
                        }}
                        disabled={submitting}
                        className="w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 py-2 text-slate-900 focus:outline-none"
                        placeholder="e.g. 5"
                      />
                    </div>

                    <p className="mt-1 text-xs text-slate-500">
                      Stored as <code>num_users</code> in the tenant database.
                    </p>
                  </div>

                  <div className="md:col-span-1">
                    <label className="mb-1 block text-sm font-medium text-slate-700">
                      Visit Client Limit
                    </label>

                    <div className="relative">
                      <UsersIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

                      <input
                        type="number"
                        min={0}
                        step={1}
                        value={numClients}
                        onChange={(event) => {
                          const value = Number(event.target.value);

                          setNumClients(
                            Number.isInteger(value) && value >= 0 ? value : 0
                          );
                        }}
                        disabled={submitting}
                        className="w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 py-2 text-slate-900 focus:outline-none"
                        placeholder="e.g. 50"
                      />
                    </div>

                    <p className="mt-1 text-xs text-slate-500">
                      Stored as <code>num_clients</code>.
                    </p>
                  </div>

                  {selectedProductLabel === "Sentinel" && (
                    <div className="md:col-span-1">
                      <label className="mb-1 block text-sm font-medium text-slate-700">
                        Sentinel License Limit
                      </label>

                      <div className="relative">
                        <Boxes className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

                        <input
                          type="number"
                          min={0}
                          step={1}
                          value={numLicenses}
                          onChange={(event) => {
                            const value = Number(event.target.value);

                            setNumLicenses(
                              Number.isInteger(value) && value >= 0 ? value : 0
                            );
                          }}
                          disabled={submitting}
                          className="w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 py-2 text-slate-900 focus:outline-none"
                          placeholder="e.g. 25"
                        />
                      </div>

                      <p className="mt-1 text-xs text-slate-500">
                        Stored as <code>num_licenses</code>.
                      </p>
                    </div>
                  )}

                  <div className="md:col-span-1">
                    <label className="mb-1 block text-sm font-medium text-slate-700">
                      Map Provider
                    </label>

                    <select
                      value={userMap}
                      onChange={(event) => setUserMap(event.target.value)}
                      disabled={submitting}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:outline-none"
                    >
                      <option value="google">Google Maps</option>
                      <option value="mapbox">Mapbox</option>
                      <option value="none">No Map / Basic</option>
                    </select>

                    <p className="mt-1 text-xs text-slate-500">
                      Stored as <code>user_map</code>.
                    </p>
                  </div>

                  <div className="md:col-span-1">
                    <label className="mb-1 block text-sm font-medium text-slate-700">
                      Payroll Enabled
                    </label>

                    <select
                      value={payrollEnabled ? "1" : "0"}
                      onChange={(event) =>
                        setPayrollEnabled(event.target.value === "1")
                      }
                      disabled={submitting}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:outline-none"
                    >
                      <option value="0">No</option>
                      <option value="1">Yes</option>
                    </select>

                    <p className="mt-1 text-xs text-slate-500">
                      Yes is stored as <code>1</code>, and No as <code>0</code>.
                    </p>
                  </div>

                  <div className="md:col-span-1">
                    <label className="mb-1 block text-sm font-medium text-slate-700">
                      Notification Source
                    </label>

                    <div className="relative">
                      <BellRing className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

                      <select
                        value={notificationSource}
                        onChange={(event) =>
                          setNotificationSource(event.target.value)
                        }
                        disabled={submitting}
                        className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-9 pr-3 text-slate-900 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-200"
                      >
                        <option value="whatsapp">WhatsApp</option>
                        <option value="mail">Mail</option>
                        <option value="both">Both</option>
                      </select>
                    </div>

                    <p className="mt-1 text-xs text-slate-500">
                      Stored as <code>notification_source</code> in the tenant database.
                    </p>
                  </div>

                  {selectedProductLabel === "Sentinel" && (
                    <div className="md:col-span-2 rounded-xl border border-rose-200 bg-rose-50/60 p-4">
                      <div className="mb-4 flex items-center gap-2">
                        <Siren className="h-5 w-5 text-rose-600" />

                        <div>
                          <h4 className="text-sm font-semibold text-slate-800">
                            Panic Call Settings
                          </h4>

                          <p className="text-xs text-slate-500">
                            Configure the tenant-specific Plivo caller number, recipients, and the spoken message.
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-4">
                        <div>
                          <label className="mb-1 block text-sm font-medium text-slate-700">
                            Plivo Panic From Number
                          </label>

                          <div className="relative">
                            <Phone className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

                            <input
                              value={panicFromNumber}
                              onChange={(event) =>
                                setPanicFromNumber(event.target.value)
                              }
                              placeholder="+14155552671"
                              disabled={submitting}
                              className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-9 pr-3 text-slate-900 placeholder:text-slate-400 focus:border-rose-400 focus:ring-2 focus:ring-rose-200"
                            />
                          </div>

                          <p className="mt-1 text-xs text-slate-500">
                            Enter the Plivo-owned number in E.164 format.
                          </p>
                        </div>

                        <div>
                          <div className="mb-2 flex items-center justify-between gap-3">
                            <label className="block text-sm font-medium text-slate-700">
                              Panic Call Recipient Numbers
                            </label>

                            <button
                              type="button"
                              onClick={() =>
                                setPanicRecipientNumbers((current) => [
                                  ...current,
                                  "",
                                ])
                              }
                              disabled={submitting}
                              className="inline-flex items-center gap-1 rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-rose-700 disabled:opacity-60"
                            >
                              <Plus className="h-3.5 w-3.5" />
                              Add Number
                            </button>
                          </div>

                          <div className="space-y-2">
                            {panicRecipientNumbers.map((number, index) => (
                              <div key={index} className="flex items-center gap-2">
                                <div className="relative flex-1">
                                  <Phone className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

                                  <input
                                    value={number}
                                    onChange={(event) => {
                                      const value = event.target.value;

                                      setPanicRecipientNumbers((current) =>
                                        current.map((item, itemIndex) =>
                                          itemIndex === index ? value : item
                                        )
                                      );
                                    }}
                                    placeholder={`Recipient ${index + 1} - +919876543210`}
                                    disabled={submitting}
                                    className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-9 pr-3 text-slate-900 placeholder:text-slate-400 focus:border-rose-400 focus:ring-2 focus:ring-rose-200"
                                  />
                                </div>

                                <button
                                  type="button"
                                  onClick={() =>
                                    setPanicRecipientNumbers((current) => {
                                      const next = current.filter(
                                        (_, itemIndex) => itemIndex !== index
                                      );

                                      return next.length ? next : [""];
                                    })
                                  }
                                  disabled={submitting}
                                  className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-rose-200 bg-white text-rose-600 hover:bg-rose-100 disabled:opacity-60"
                                  aria-label={`Remove recipient ${index + 1}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            ))}
                          </div>

                          <p className="mt-2 text-xs text-slate-500">
                            These numbers will be stored as separate rows and called when a Sentinel user triggers panic.
                          </p>
                        </div>

                        <div>
                          <label className="mb-1 block text-sm font-medium text-slate-700">
                            Panic Call Voice Message
                          </label>

                          <div className="relative">
                            <MessageSquareText className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />

                            <textarea
                              value={panicVoiceMessageTemplate}
                              onChange={(event) =>
                                setPanicVoiceMessageTemplate(event.target.value)
                              }
                              rows={3}
                              maxLength={500}
                              placeholder={DEFAULT_PANIC_VOICE_TEMPLATE}
                              disabled={submitting}
                              className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-9 pr-3 text-slate-900 placeholder:text-slate-400 focus:border-rose-400 focus:ring-2 focus:ring-rose-200"
                            />
                          </div>

                          <p className="mt-1 text-xs text-slate-500">
                            This is exactly what gets spoken on the panic call. Use{" "}
                            <code>{"{id}"}</code> for the guard ID and{" "}
                            <code>{"{name}"}</code> for the guard name — both are filled in automatically when a panic call is placed.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="md:col-span-2">
                    <label className="mb-1 block text-sm font-medium text-slate-700">
                      Company Logo
                    </label>

                    <div className="flex flex-col sm:flex-row items-start gap-4">
                      <div className="w-40 h-40 shrink-0 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
                        {logoPreview ? (
                          <img
                            src={logoPreview}
                            alt="Logo preview"
                            className="max-w-full max-h-full object-contain"
                          />
                        ) : logoPath ? (
                          <img
                            src={logoPath}
                            alt="Logo"
                            className="max-w-full max-h-full object-contain"
                          />
                        ) : (
                          <span className="text-xs text-slate-400">No logo</span>
                        )}
                      </div>

                      <div className="flex-1 w-full">
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/jpg,image/webp"
                          onChange={handleLogoChange}
                          disabled={submitting}
                          className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-200"
                        />

                        <p className="mt-1 text-xs text-slate-500">
                          PNG, JPG or WEBP. Maximum size: 2MB.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-4 gap-3">
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                      <div className="text-xs text-slate-500">Tenant</div>

                      <div className="text-sm font-medium text-slate-800 flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-slate-500" />
                        {selected?.TenantCode || "—"}
                      </div>
                    </div>

                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                      <div className="text-xs text-slate-500">Expires</div>

                      <div className="text-sm font-medium text-slate-800 flex items-center gap-2">
                        <CalendarDays className="h-4 w-4 text-slate-500" />
                        {selected?.ExpiresOn ?? "—"}
                      </div>
                    </div>

                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                      <div className="text-xs text-slate-500">Max Users</div>

                      <div className="text-sm font-medium text-slate-800 flex items-center gap-2">
                        <UsersIcon className="h-4 w-4 text-slate-500" />
                        {selected?.MaxUsers ?? "—"}
                      </div>
                    </div>

                    <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2">
                      <div className="text-xs text-blue-600">Member Prefix</div>

                      <div className="text-sm font-bold text-blue-900 flex items-center gap-2">
                        <Hash className="h-4 w-4 text-blue-600" />
                        {allocatedMemberPrefix || "Auto"}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="sticky bottom-0 bg-white/80 backdrop-blur supports-[backdrop-filter]:bg-white/60 border-t border-slate-200">
                  <div className="px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row items-center gap-3 sm:justify-between">
                    <p className="text-xs text-slate-600">
                      Member Prefix is automatically allocated by the Super Admin backend.
                    </p>

                    <div className="flex items-center gap-2">
                      <button
                        type="submit"
                        disabled={submitting}
                        className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500/30 disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        <BadgeCheck className="h-4 w-4" />
                        {submitting ? "Saving..." : "Create Admin & Company"}
                      </button>

                      <button
                        type="button"
                        onClick={() => navigate("/superAdmin/admin_Menu")}
                        disabled={submitting}
                        className="inline-flex items-center gap-2 rounded-lg bg-rose-600 px-4 py-2 text-white shadow hover:bg-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-500/30 disabled:opacity-60"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}